import {
    Alert,
    DialogTitle,
} from "@mui/material";
import {SegmentedToggle} from "../../../../misc/SegmentedToggle";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {useAsync2, useAsyncAction} from "nate-react-api-helpers";
import {api} from "../../../../api/API";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {useProjectId} from "../../shopdrawing/ProjectName";
import React, {useState} from "react";
import {QuoteHwForceUpdate} from "./QuoteHardware";
import {useAlternative} from "../alternative/Alternative";
import {QuoteDiv10ForceUpdate} from "../div10/Div10";
import {useIsQuote} from "../QuoteContainer";
import {Div10ForceUpdate} from "../../shopdrawing/div10/Div10";
import {HwForceUpdate} from "../../shopdrawing/hardware/Hardware";
import {getAll} from "../../../logistics/routes/useGetAll";
import {TransferProductWorkTable} from "./TransferProductWorkTable";
import {HwLibraryTypeProvider} from "../../shopdrawing/hardware/library/HwLibraryType";
import {PopperZIndexProvider} from "../../../../misc/scroller/PopperZIndexProvider";
import {
    buildDiv10Submit,
    buildSubmitItems,
    DynamicMode,
    SourceItem,
    sourceItemsFromDiv10,
    sourceItemsFromHardware
} from "./transferProductRows";
import {useTransferEdits} from "./useTransferEdits";
import {BulkEditItem, ProductReplacement} from "../../../../api/QuoteHardware";

export type {ProductItem} from "./transferProductRows";

type SubmitInput = {
    project: number;
    alternative: number;
    items: BulkEditItem[];
    div10: {
        replacements: ProductReplacement[];
        removals: number[];
    };
}

export function TransferProductModal(props: {
    div10?: boolean;
    onDone(): void;
}) {
    const onDoneRef = useSyncedRef(props.onDone);
    const isDiv10 = useSyncedRef(props.div10);
    const isQuote = useIsQuote()
    const isQuoteRef = useSyncedRef(isQuote);

    const action = useAsyncAction(async (input: SubmitInput) => {
        if(isQuoteRef.current) {
            if (isDiv10.current) {
                await api.quoteDiv10.replaceProducts({project: input.project, alternative: input.alternative, ...input.div10})
                QuoteDiv10ForceUpdate.emit(Date.now());
            } else {
                await api.quoteHardware.bulkEditProduct({project: input.project, alternative: input.alternative, items: input.items})
                QuoteHwForceUpdate.emit(Date.now());
            }
        } else {
            if (isDiv10.current) {
                await api.div10.replaceProducts({project: input.project, ...input.div10})
                Div10ForceUpdate.emit(Date.now());
            } else {
                await api.hardware.bulkEditProduct({project: input.project, items: input.items})
                HwForceUpdate.emit(Date.now());
            }
        }

        onDoneRef.current();
    }, []);

    const project = useProjectId();
    const alternative = useAlternative();

    const list = useAsync2(
        async (input): Promise<SourceItem[]> =>  {
            if(isDiv10.current) {
                const list = await getAll(null, offset =>
                    isQuoteRef.current ?
                        api.quoteDiv10.list(Object.assign({}, input, {
                            offset: offset,
                            tab: "wr-accessory",
                        })) :
                        api.div10.list(Object.assign({}, input, {
                            offset: offset,
                            tab: "wr-accessory",
                        })))

                if(!list) return [];
                return sourceItemsFromDiv10(list);
            }

            const list = await (isQuoteRef.current ? api.quoteHardware.listWithContents(input) : api.hardware.listWithContents(input))
            if(!list) return [];
            return sourceItemsFromHardware(list);
        },
        {
            project,
            alternative,
        }, [project, alternative]);

    const edits = useTransferEdits(list.asList, list.reload);

    const [ribbonEl, setRibbonEl] = useState<HTMLDivElement | null>(null);

    const submit = () => {
        const items = buildSubmitItems(list.asList, edits.edits);
        const div10 = buildDiv10Submit(list.asList, edits.edits);

        const hasChanges = props.div10 ?
            div10.replacements.length > 0 || div10.removals.length > 0 :
            items.length > 0;

        if(!hasChanges) {
            props.onDone();
            return
        }

        action.callback({
            project: project,
            alternative: alternative,
            items: items,
            div10: div10,
        });
    }

    return (
        <Dialog2 onClose={props.onDone} open maxWidth="xl" fullWidth onEnterPress={submit}>
            <DialogTitle>
                <div style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                }}>
                    <div>Edit by Product</div>
                    <div ref={setRibbonEl} />
                </div>
            </DialogTitle>

            {edits.error && <Alert severity="error">{edits.error}</Alert>}

            <div style={{
                height: "calc(90vh - 200px)",
                display: "flex",
                flexDirection: "column",
            }}>
                <HwLibraryTypeProvider value={props.div10 ? "div10/wr-accessory" : "hardware"}>
                    <PopperZIndexProvider value={1400}>
                        {(!list.loading || !!list.result) && edits.rows.length > 0 && <TransferProductWorkTable
                            value={edits.rows}
                            onChange={edits.change}
                            onProductSaved={edits.productSaved}
                            conflictKeys={edits.conflictKeys}
                            div10={props.div10}
                            ribbonContainer={ribbonEl}
                            ribbonStart={!props.div10 && <SegmentedToggle<DynamicMode>
                                label="Dynamic:"
                                value={edits.mode}
                                onChange={edits.setMode}
                                options={[
                                    {value: "grouped", label: "Grouped"},
                                    {value: "all", label: "All"},
                                ]}
                            />}
                        />}
                    </PopperZIndexProvider>
                </HwLibraryTypeProvider>
            </div>

            <SmoothActionBar
                loading={action.loading}
                error={action.error}
                actionName="Apply"
                onCancel={props.onDone}
                onSubmit={submit}
            />
        </Dialog2>
    )
}
